/**
 * @fileoverview Componente optimizado ChatAttachMenuMC para menú de adjuntos en el chat.
 * Mejora de renderizado: memoización de handlers, clases y estructura para evitar re-renders innecesarios.
 * @module ChatAttachMenuMC
 * @category Component - Molecule
 * @since 2.0.0
 * @version 2.1.0
 */
import React, {
  memo,
  useCallback,
  useRef,
  useMemo
} from 'react';
import { useTranslation } from 'react-i18next';
import AttachFileIcon from '../../../../components/icons/AttachFileIcon';
import AttachImageIcon from '../../../../components/icons/AttachImageIcon';
import AttachVideoIcon from '../../../../components/icons/AttachVideoIcon';
import SendLocationIcon from '../../../../components/icons/SendLocationIcon';
import ViewProductsIcon from '../../../../components/icons/ViewProductsIcon';
import GenerateOrderIcon from '../../../../components/icons/GenerateOrderIcon';
import { MAX_IMAGE_SIZE_TRANSFER_WS } from '../../../../constants/chatFileSizes';
import { sharingInformationService } from '../../../../services/sharingInformationService';
import useEnsureInboxCustomer from '../../../hooks/inbox/actions/useEnsureInboxCustomer';
import {
  isTikTokCommentsPlatform,
  isTikTokPlatform
} from '../../../hooks/inbox/constants/platformMessageRestrictions';

/**
 * ChatAttachMenuMC - Componente para menú de adjuntos en el chat.
 */
const ChatAttachMenuMC = memo(({
  handleSubmit,
  setOpenAttachMenu,
  openModal,
  onSendPdf,
  onSendPaymentLink,
  conversation,
  platform
}) => {
  // UI Layer: Estado y handlers visuales
  const { t } = useTranslation('InboxMC');
  const { ensureInboxCustomer: ensureCustomer } = useEnsureInboxCustomer(conversation);
  const inputAttachFileRef = useRef(null);
  const inputAttachVideoRef = useRef(null);

  // TailwindCSS: clase de opción memoizada
  const containerOptionClassName = useMemo(() => (
    "tw-flex tw-items-center tw-text-sm tw-mb-4 tw-cursor-pointer"
  ), []);

  // UI Layer: Handlers visuales
  const handleCloseMenu = useCallback(() => {
    setOpenAttachMenu(false);
  }, [setOpenAttachMenu]);

  // UI Layer: Handlers de input
  const handleAttachFileClick = useCallback(() => {
    inputAttachFileRef.current?.click();
  }, []);

  const handleAttachVideoClick = useCallback(() => {
    inputAttachVideoRef.current?.click();
  }, []);

  // UI Layer: Handlers de input onChange memoizados
  const handleFileChange = useCallback((e) => {
    handleSubmit('file', e.target.files[0]);
  }, [handleSubmit]);

  const handleVideoChange = useCallback((e) => {
    handleSubmit('video', e.target.files[0]);
  }, [handleSubmit]);

  // UI Layer: Opciones de menú memoizadas
  const menuOptions = useMemo(() => {
    const isTikTok = isTikTokPlatform(platform);
    const imageMaxFiles = isTikTokCommentsPlatform(platform) ? 1 : 5;
    const options = [
      {
        key: 'file',
        icon: <AttachFileIcon className="mr-10" />,
        label: t('attachMenu.file'),
        onClick: handleAttachFileClick
      },
      {
        key: 'image',
        icon: <AttachImageIcon className="fill-navy-blue w-32 mr-10" />,
        label: t('attachMenu.image'),
        onClick: () => {
          handleCloseMenu();
          openModal({
            title: t('attachMenu.image'),
            accept: "image/png,image/jpeg",
            maxFiles: imageMaxFiles,
            maxFileSize: MAX_IMAGE_SIZE_TRANSFER_WS,
            handleSubmit: (attachments) => handleSubmit('image', attachments)
          }, 'AttachFilesSidebar', { position: 'center' });
        }
      },
      {
        key: 'video',
        icon: <AttachVideoIcon className="fill-orange w-32 mr-10" />,
        label: t('attachMenu.video'),
        onClick: handleAttachVideoClick
      },
      {
        key: 'location',
        icon: <SendLocationIcon className="mr-10" />,
        label: t('attachMenu.location'),
        onClick: () => {
          handleCloseMenu();
          openModal({
            handleSubmit: (location) => {
              if (isTikTok) {
                const url = `https://www.google.com/maps/place/${location.latitude},${location.longitude}`;
                handleSubmit('text', { message: url });
              } else {
                handleSubmit('location', location);
              }
            }
          }, 'LocationPickerSidebar', { position: 'center' });
        }
      },
      {
        key: 'products',
        icon: <ViewProductsIcon className="mr-10" />,
        label: t('attachMenu.products'),
        onClick: () => {
          handleCloseMenu();
          sharingInformationService.setSubject({
            openProductSelection: true
          });
          sharingInformationService.setSubject({});
        }
      },
      {
        key: 'order',
        icon: <GenerateOrderIcon className="mr-10" />,
        label: t('attachMenu.order'),
        onClick: async () => {
          handleCloseMenu();
          const result = await ensureCustomer();
          if (!result.success) return;
          openModal(
            {
              onSendPaymentLink,
              onSendPdf,
              action: 'new'
            },
            'OrderModal',
            { className: "tw-w-[60vw]" }
          );
        }
      }
    ];

    if (isTikTok) {
      return options.filter((o) => o.key !== 'file' && o.key !== 'order' && o.key !== 'video');
    }
    return options;
  }, [
    handleAttachFileClick,
    handleAttachVideoClick,
    openModal,
    handleSubmit,
    handleCloseMenu,
    onSendPdf,
    onSendPaymentLink,
    ensureCustomer,
    t,
    platform
  ]);

  // UI Layer: Renderizado
  return (
    <>
      <div
        className="tw-fixed tw-inset-0 tw-bg-transparent tw-bg-opacity-30 tw-z-40"
        onClick={handleCloseMenu}
        aria-label={t('attachMenu.close')}
        tabIndex={-1}
      />
      <input
        ref={inputAttachFileRef}
        id="attach-file"
        className="d-none"
        type="file"
        name="messageFile"
        accept={"application/pdf, application/msword, "
          + "application/vnd.openxmlformats-officedocument.wordprocessingml.document, "
          + "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, "
          + "application/vnd.ms-excel, audio/mpeg"}
        onChange={handleFileChange}
      />
      <input
        ref={inputAttachVideoRef}
        id="attach-video"
        className="d-none"
        type="file"
        name="messageVideo"
        accept="video/mpeg4, video/mp4, video/quicktime, video/avi, video/webm"
        onChange={handleVideoChange}
      />
      <div
        className="tw-absolute tw-left-0 tw-bottom-full tw-mb-2 tw-z-50 tw-bg-white tw-rounded-xl tw-py-2.5 tw-px-4 tw-shadow-lg tw-min-w-72 tw-max-w-[90vw] tw-inline-block"
        role="dialog"
        aria-modal="true"
      >
        <div className="tw-text-base tw-font-semibold tw-mb-4">{t('attachMenu.title')}</div>
        {menuOptions.map((option) => (
          <div
            key={option.key}
            className={containerOptionClassName}
            onClick={option.onClick}
            role="button"
            tabIndex={0}
            aria-label={option.label}
            style={option.onClick ? undefined : { opacity: 0.6, cursor: 'default' }}
          >
            {option.icon}
            <span>{option.label}</span>
          </div>
        ))}
      </div>
    </>
  );
});

ChatAttachMenuMC.displayName = 'ChatAttachMenuMC';

export default ChatAttachMenuMC;
